<ion-content>
  <div class="playground">

    <div class="playground__request">
      <h2>{{ 'playground.request' | translate }}</h2>

      <ion-item>
        <ion-select [label]="'playground.endpoint' | translate" labelPlacement="stacked" interface="popover"
                    [(ngModel)]="selectedEndpointIndex" (ionChange)="onEndpointChange($event.detail.value)">
          @for (v of visibleEndpoints; track v.endpoint.path + v.endpoint.method) {
          <ion-select-option [value]="v.index">{{ v.endpoint.method }} {{ v.endpoint.path }}</ion-select-option>
          }
        </ion-select>
      </ion-item>

      <div class="request-line">
        <ion-badge [color]="methodColor(method)">{{ method }}</ion-badge>
        <ion-input class="request-line__path" [(ngModel)]="path" placeholder="/pfad/{id}"></ion-input>
      </div>

      @if (tokens.length > 0) {
      <ion-item>
        <ion-select [label]="'playground.tokenLabel' | translate" labelPlacement="stacked"
                    interface="popover" [(ngModel)]="selectedTokenId" (ionChange)="onTokenChange()">
          @for (t of tokens; track t.id) {
          <ion-select-option [value]="t.id">{{ t.name }} ({{ t.tokenPrefix }}…)</ion-select-option>
          }
        </ion-select>
      </ion-item>
      }

      <ion-item>
        <ion-input [label]="'playground.pasteTokenLabel' | translate" labelPlacement="stacked"
                   [placeholder]="'playground.pasteTokenPlaceholder' | translate"
                   [(ngModel)]="pastedToken"></ion-input>
      </ion-item>

      @if (method !== 'GET' && method !== 'DELETE') {
      <div class="body-editor">
        <label class="body-editor__label">{{ 'playground.bodyLabel' | translate }}</label>
        <app-json-editor [(value)]="bodyText" [jsonSchema]="currentBodySchema"></app-json-editor>
      </div>
      }

      @if (requestError) {
      <p class="request-error">{{ requestError }}</p>
      }

      <div class="request-actions">
        <ion-button expand="block" (click)="send()" [disabled]="isSending">
          @if (isSending) { <ion-spinner name="dots"></ion-spinner> } @else { {{ 'playground.send' | translate }} }
        </ion-button>
        <ion-button fill="outline" (click)="openSaveCollectionForm()">
          <ion-icon name="bookmark-outline" slot="start"></ion-icon>
          {{ 'playground.saveAsCollection' | translate }}
        </ion-button>
      </div>

      @if (showSaveCollectionForm) {
      <div class="save-collection-form">
        <ion-item>
          <ion-input [label]="'tokens.form.name' | translate" [placeholder]="'playground.collectionNamePlaceholder' | translate"
                     labelPlacement="stacked" [(ngModel)]="newCollectionName"></ion-input>
        </ion-item>
        <div class="save-collection-form__actions">
          <ion-button fill="clear" (click)="cancelSaveCollection()">{{ 'common.cancel' | translate }}</ion-button>
          <ion-button (click)="saveCollection()" [disabled]="isSavingCollection">{{ 'common.save' | translate }}</ion-button>
        </div>
      </div>
      }

      <!-- Collections -->
      <div class="section-title">{{ 'playground.collections' | translate }}</div>
      @if (collections.length === 0) {
      <p class="empty-hint">{{ 'playground.collectionsEmpty' | translate }}</p>
      } @else {
      <div class="collection-list">
        @for (c of collections; track c.id) {
        <div class="collection-row">
          <a class="collection-row__name" (click)="loadCollection(c)">{{ c.name }}</a>
          <ion-button size="small" fill="clear" color="danger" (click)="deleteCollection(c)">
            <ion-icon name="trash-outline" slot="icon-only"></ion-icon>
          </ion-button>
        </div>
        }
      </div>
      }

      <!-- History -->
      <div class="section-title section-title--clickable" (click)="toggleHistory()">
        {{ 'playground.history' | translate }}
        <ion-icon [name]="showHistory ? 'chevron-up-outline' : 'chevron-down-outline'"></ion-icon>
      </div>
      @if (showHistory) {
        @if (history.length === 0) {
        <p class="empty-hint">{{ 'playground.historyEmpty' | translate }}</p>
        } @else {
        <div class="history-list">
          @for (h of history; track h.id) {
          <a class="history-row" (click)="loadFromHistory(h)">
            <ion-badge [color]="methodColor(h.method)">{{ h.method }}</ion-badge>
            <code class="history-row__path">{{ h.path }}</code>
            <ion-badge [color]="statusColor(h.status)">{{ h.status }}</ion-badge>
            <span class="history-row__time">{{ h.occurredAt | date:'short' }}</span>
          </a>
          }
        </div>
        <ion-button size="small" fill="clear" (click)="clearHistory()">{{ 'playground.clearHistory' | translate }}</ion-button>
        }
      }
    </div>

    <div class="playground__response">
      <div class="response-header">
        <ion-segment [(ngModel)]="rightPanelView" mode="md">
          <ion-segment-button value="response">{{ 'playground.response' | translate }}</ion-segment-button>
          <ion-segment-button value="code">{{ 'playground.code' | translate }}</ion-segment-button>
        </ion-segment>
      </div>

      @if (rightPanelView === 'response') {
        @if (!response) {
        <div class="empty-response">{{ 'playground.noRequestSent' | translate }}</div>
        } @else {
        <div class="response-status">
          <ion-badge [color]="statusColor(response.status)">{{ response.status || ('playground.error' | translate) }} {{ response.statusText }}</ion-badge>
          <span class="response-status__timing">{{ response.durationMs }} ms</span>
        </div>

        <pre class="response-body">{{ response.body }}</pre>

        <details class="response-headers">
          <summary>{{ 'playground.headersCount' | translate:{ count: response.headers.length } }}</summary>
          @for (h of response.headers; track h.key) {
          <div class="response-headers__row"><code>{{ h.key }}</code>: {{ h.value }}</div>
          }
        </details>
        }
      } @else {
        <div class="snippet-langs">
          @for (lang of snippetLanguages; track lang.id) {
          <button type="button" class="snippet-lang" [class.snippet-lang--active]="snippetLanguage === lang.id"
                  (click)="snippetLanguage = lang.id">{{ lang.label }}</button>
          }
        </div>
        <div class="snippet-toolbar">
          <ion-button size="small" fill="clear" (click)="copySnippet()">
            <ion-icon name="copy-outline" slot="start"></ion-icon>
            {{ 'common.copy' | translate }}
          </ion-button>
        </div>
        <pre class="response-body">{{ snippet }}</pre>
      }
    </div>

  </div>
</ion-content>
